import { toast } from '@/hooks/use-toast';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import type { Deal } from '@/models/registry';
import { dealStore } from '@/models/stores';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { Button } from '../ui/button';
import InlineTextInput from '../ui/inline-text-input';
import KeyboardKeyIcon from '../ui/keyboard-key-icon';
import { PDKCDKIcon } from '../ui/pdk-icons';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

const CDKIdEditor = observer(({ deal }: { deal: Deal }) => {
  const cdkId = deal.cdk_id;
  const dealId = deal.id;

  const [cdkIdValue, setCdkIdValue] = useState(!cdkId ? '' : cdkId.toString());

  const queryClient = useQueryClient();

  const updateCdkIdMutation = useMutation({
    onMutate: async ({ cdkId }) => {
      if (!dealId) return;

      dealStore.updateCdkId({
        id: dealId,
        cdkId,
      });
    },
    mutationFn: async ({ cdkId }: { cdkId: Deal['cdk_id'] }) => {
      const response = await pdkAxios
        .post(`/deals/${dealId}/cdk-id`, { cdk_id: cdkId })
        .catch((e) => {
          console.error('Error updating CDK ID', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {},
    onError: (error, variables) => {
      console.error('Error updating CDK ID', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', deal.short_id] });
    },
  });

  const handleCdkIdChange = useCallback(
    debounce((newCdkId: Deal['cdk_id']) => {
      updateCdkIdMutation.mutate({
        cdkId: newCdkId,
      });
    }, 1000),
    [],
  );

  useEffect(() => {
    if (cdkIdValue === (cdkId?.toString() || '')) return;

    const newCdkId = parseInt(cdkIdValue);

    handleCdkIdChange(newCdkId);
  }, [cdkIdValue]);

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCdkIdValue(e.target.value);
  }, []);

  useEffect(() => {
    setCdkIdValue(cdkId?.toString() || '');
  }, [cdkId]);

  const inputRef = useRef<HTMLInputElement>(null);
  useHotkeys(HOTKEYS.DEAL_CDK_ID, (e) => {
    e.preventDefault();
    e.stopPropagation();
    inputRef.current?.focus();
  });

  return (
    <div className="flex flex-row gap-3 items-center">
      <Tooltip delayDuration={500}>
        <TooltipTrigger asChild>
          <Button variant="ghost" className="p-1 h-fit">
            <PDKCDKIcon
              className="cursor-pointer"
              onClick={() => {
                navigator.clipboard.writeText(cdkId?.toString() || '');

                toast({
                  title: 'CDK ID copied to clipboard',
                });
              }}
            />
            <InlineTextInput
              className="rounded text-xs"
              placeholder="Add CDK ID"
              value={cdkIdValue || ''}
              onChange={onChange}
              ref={inputRef}
              onFocus={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            />
          </Button>
        </TooltipTrigger>
        <TooltipContent className="rounded px-2 py-2 border  z-50 text-xs flex flex-row gap-2 items-center">
          CDK ID <KeyboardKeyIcon>{HOTKEYS.DEAL_CDK_ID}</KeyboardKeyIcon>
        </TooltipContent>
      </Tooltip>
    </div>
  );
});

export default CDKIdEditor;
